import React, { ReactNode, useCallback } from 'react';
import { isMobile } from 'react-device-detect';

import { useMytaverseEvent } from '../../../../providers';
import { sendCustomSentryBreadcrumb } from '@helpers/sentry';
import WarningButtonBadge from '../../../WarningButtonBadge';
import ShareWebcamButtonNew from './ShareWebcamButtonNew/ShareWebcamButton';
import { useConferenceState } from '@hooks/conferenceContext';
import { useCommunications } from '@core/communications/providers/CommunicationsProvider';
import { AnalyticsType } from '@interfaces/analytics';

const WebcamSharing = () => {
  const { trackEventAnalytics, currentParticipant } = useMytaverseEvent();

  const { handleToggleVideoSharing, isCameraOn, cameraList } =
    useCommunications();

  const { activeCameraDeviceId } = useConferenceState();

  const cameraDeviceId = cameraList.length ? cameraList[0]?.deviceId : '';

  const skipInitialAnalyticsRef = React.useRef(true);

  const handleVideoClick = async () => {
    sendCustomSentryBreadcrumb({
      message: `${!isCameraOn ? 'start' : 'stop'} video, participantId=${
        currentParticipant?.participantId
      }`,
    });

    await handleToggleVideoSharing();
  };

  const showWarningButtonBadge = useCallback(
    (icon: ReactNode) => {
      return (
        <WarningButtonBadge isHidden={!!cameraDeviceId}>
          {icon}
        </WarningButtonBadge>
      );
    },
    [cameraDeviceId, isMobile],
  );

  // isCameraOn flips only after Zoom's startVideo/stopVideo succeed, so a failed
  // start is never reported as CameraOn.
  React.useEffect(() => {
    if (skipInitialAnalyticsRef.current) {
      skipInitialAnalyticsRef.current = false;
      return;
    }

    trackEventAnalytics(
      isCameraOn ? AnalyticsType.CameraOn : AnalyticsType.CameraOff,
    );
  }, [isCameraOn, trackEventAnalytics]);

  return (
    <ShareWebcamButtonNew
      cameraDeviceId={cameraDeviceId ? (activeCameraDeviceId as string) : ''}
      onVideoClick={handleVideoClick}
      showWarningBadge={showWarningButtonBadge}
    />
  );
};

export default WebcamSharing;
